import { useEffect, useMemo } from "react";
import {
  MaterialReactTable,
  useMaterialReactTable,
  MRT_TablePagination,
  MRT_TopToolbar,
} from "material-react-table";
import { loadIcon } from "@iconify/react";
import { Box } from "@mui/material";

//nested data is ok, see accessorKeys in ColumnDef below
const defaultPagination = {
  pageIndex: 0,
  pageSize: 10,
};

const MRTTable = ({
  data,
  columns,
  renderRowActions,
  renderTopToolbarCustomActions,
  pageSize = 5,
  loading,
  pagination = defaultPagination,
  enableRowSelection,
  onSelectionChange,
  expanded,
  ...others
}) => {
  const table = useMaterialReactTable({
    columns,
    data,
    // enableColumnResizing: true,
    enableColumnActions: true,
    enableRowActions: true,
    positionActionsColumn: "last",
    enableDensityToggle: false,
    // positionPagination: "top",
    enableColumnPinning: true,
    enableGlobalFilter: true,
    enableColumnFilters: true,
    enableDensityToggle: true,
    enableRowSelection: enableRowSelection || false,
    enableFullScreenToggle: false,
    layoutMode: "grid",
    enableStickyHeader: true,
    rowPinningDisplayMode: "select-sticky",
    displayColumnDefOptions: { "mrt-row-actions": { size: 170 } },

    //  enableGrouping:true,
    initialState: {
      density: "comfortable",
      columnPinning: { left: ["mrt-row-actions"], right: [] },
      expanded: expanded || false,
    },
    state: { showSkeletons: loading, showProgressBars: loading, pagination },
    muiTableHeadRowProps: {
      sx: {
        background: (theme) =>
          `${
            theme.palette.mode === "light"
              ? theme.palette.grey[200]
              : theme.palette.grey[700]
          }  !important`,
      },
    },
    muiTableBodyRowProps: ({ row }) => ({
      "data-testid": `row-${row.id}`,
      sx: {
        "&.MuiTableRow-root.Mui-selected .MuiTableCell-body": {
          background: (theme) => theme.palette.primary.lighter,
          color: "black",
        },
      },
    }),
    muiTableContainerProps: {
      sx: {
        maxHeight: "none",
        overflow: "unset", 
      },
    },
    muiPaginationProps: {
      sx: {
        "& .MuiToolbar-root": {
          justifyContent: "flex-start",
          paddingLeft: 0,
        },
      },
    },

    muiTopToolbarProps: {
      sx: {
        justifyContent: "flex-start",
        gap: 2,
      },
    },
    muiBottomToolbarProps: {
      sx: {
        justifyContent: "flex-start",
        gap: 2,
      },
    },
    muiTableHeadCellProps: {
      sx: {
        color: (theme) =>
          theme.palette.mode === "light"
            ? theme.palette.grey[600]
            : theme.palette.grey[200],
        paddingLeft: "4px",
        paddingRight: "8px",
        fontWeight: 600,
        boxShadow: "none",
        background: (theme) =>
          `${
            theme.palette.mode === "light"
              ? theme.palette.grey[200]
              : theme.palette.grey[700]
          }  !important`,
        "& .Mui-TableHeadCell-Content": {
          justifyContent: "space-between",
        },
      },
    },
    muiTableBodyCellProps: {
      sx: { paddingLeft: "4px", paddingRight: "8px", boxShadow: "none" },
    },
    muiTableHeadCellColumnActionsButtonProps: {
      sx: {
        color: (theme) => theme.palette.primary.contrastText,
        opacity: 1,
      },
    },

    renderRowActions: renderRowActions,
    renderTopToolbarCustomActions: renderTopToolbarCustomActions,
    muiDetailPanelProps: {
      sx: {
        display: "block",
        width: "100%",
        // background: (theme) => theme.palette.grey[100],
      },
    },
    renderTopToolbar: ({ table }) => (
      <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
        <MRT_TopToolbar table={table} />
    
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            borderTop: (theme) => `1px solid ${theme.palette.divider}`,
            p: 1,
          }}
        >
          <MRT_TablePagination table={table} />
        </Box>
      </Box>
    ),
    
    renderBottomToolbar: ({ table }) => (
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-start", // left aligned pagination
          p: 1,
        }}
      >
        <MRT_TablePagination table={table} />
      </Box>
    ),
    ...others,
  });

  useEffect(() => {
    table.resetRowSelection();
  }, [data, table]);

  //do something when the row selection changes...
  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(table.getSelectedRowModel().flatRows);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onSelectionChange, table.getSelectedRowModel().flatRows]);

  return <MaterialReactTable id="mrt-table" table={table} />;
};

export default MRTTable;
